import {
  isPastWeek,
  type TrayCard,
  unpublishBlockedReason,
  type Week,
} from '../_lib/auto-place'
import { formatHoursDuration } from '../_lib/duration'
import type { LibraryItem } from '../_lib/filter-engine'
import { withMockElapsed } from '../_lib/mock-week'
import type { CurriculumState } from './contracts'

const libraryMeta = (item: LibraryItem): string => {
  const assignmentMeta = (item as LibraryItem & { assignmentMeta?: unknown })
    .assignmentMeta
  if (typeof assignmentMeta === 'string') return assignmentMeta
  return `${item.topic} / ${item.subtopic} · ${item.difficulty} · ELO ${item.elo} · ~${formatHoursDuration(item.est)}`
}

export const libraryCard = (
  item: LibraryItem,
  id: string,
  extra?: Partial<TrayCard>,
): TrayCard => {
  const assignment = item as LibraryItem & { decay?: unknown }
  return {
    id,
    title: item.title,
    type: item.type,
    meta: libraryMeta(item),
    est: item.est,
    ...(item.repeat ? { repeat: item.repeat } : {}),
    ...(assignment.decay === true ? { decay: true } : {}),
    ...extra,
  }
}

export const appendCard = (
  weeks: Week[],
  weekIdx: number,
  dayIdx: number,
  card: TrayCard,
): Week[] =>
  weeks.map((week, currentWeekIdx) =>
    currentWeekIdx !== weekIdx
      ? week
      : {
          ...week,
          days: week.days.map((day, currentDayIdx) =>
            currentDayIdx !== dayIdx
              ? day
              : { ...day, cards: [...day.cards, card] },
          ),
        },
  )

// Re-exported, not redefined: this used to be a byte-identical copy of the
// predicate in `_lib/auto-place`, and the two drifting apart is how a week the
// board refuses to place into becomes one the reducer happily edits.
export { isPastWeek }

/**
 * `publish-week` and `unpublish-week`: one transition read in two directions,
 * so one function — their guards are each other's mirror and have to stay in
 * step.
 *
 * Unpublishing carries the service's own rule (LG-361 §12): a week the client
 * has already worked in stays published, whatever the board offers.
 */
export const weekPublication = (
  state: CurriculumState,
  weekId: string,
  publishing: boolean,
): CurriculumState => {
  const week = state.weeks.find(
    (candidate) =>
      candidate.id === weekId &&
      candidate.status === (publishing ? 'draft' : 'published'),
  )
  if (!week) return state
  const blocked = publishing ? null : unpublishBlockedReason(week)
  // Answered rather than ignored: the control stays pressable so the reason
  // reaches a coach who cannot hover for it.
  if (blocked) return { ...state, flash: blocked }
  const status: Week['status'] = publishing ? 'published' : 'draft'
  return {
    ...state,
    weeks: state.weeks.map((candidate) =>
      candidate.id === weekId ? { ...candidate, status } : candidate,
    ),
    flash: publishing
      ? `${week.label} published — the client can now see it.`
      : `${week.label} unpublished — it is a draft again and hidden from the client.`,
  }
}

// Shared by remove-from-day and move-card-to: finds a scheduled (non-tray)
// card by id across every non-past week and filters it out of its current
// day. Done/fixed cards are left in place (and `card` comes back undefined),
// which both callers treat as "refuse the action".
export const extractScheduledCard = (
  weeks: Week[],
  cardId: string,
): { weeks: Week[]; card?: TrayCard } => {
  let removed: TrayCard | undefined
  const nextWeeks = weeks.map((week) =>
    isPastWeek(week)
      ? week
      : {
          ...week,
          days: week.days.map((day) => ({
            ...day,
            cards: day.cards.filter((card) => {
              if (card.id !== cardId || card.done || card.fixed) return true
              removed = card
              return false
            }),
          })),
        },
  )
  return { weeks: nextWeeks, card: removed }
}

export const weekName = (week: Week): string => week.label.split(' · ')[0]

export const updateCard = (
  state: CurriculumState,
  cardId: string,
  updater: (card: TrayCard) => TrayCard | null,
  includeTray = true,
): { weeks: Week[]; tray: TrayCard[]; updated?: TrayCard } => {
  let updated: TrayCard | undefined
  const weeks = state.weeks.map((week) =>
    isPastWeek(week)
      ? week
      : {
          ...week,
          days: week.days.map((day) => ({
            ...day,
            cards: day.cards.flatMap((card) => {
              if (card.id !== cardId) return [card]
              const next = updater(card)
              if (!next) return []
              updated = next
              return [next]
            }),
          })),
        },
  )
  const tray = state.tray.flatMap((card) => {
    if (!includeTray) return [card]
    if (card.id !== cardId) return [card]
    const next = updater(card)
    if (!next) return []
    updated = next
    return [next]
  })
  return { weeks, tray, updated }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const
const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const

export const addOneWeek = (lastWeek: Week): Week => {
  const number =
    Number.parseInt(weekName(lastWeek).replace('Week ', ''), 10) + 1
  const range = lastWeek.label.match(
    /· ([A-Z][a-z]{2}) (\d+)–(?:([A-Z][a-z]{2}) )?(\d+)/,
  )
  const startMonth = Math.max(
    0,
    MONTHS.indexOf(range?.[1] as (typeof MONTHS)[number]),
  )
  const startDay = Number.parseInt(range?.[2] ?? '1', 10)
  const nextStart = new Date(Date.UTC(2020, startMonth, startDay + 7))
  const nextEnd = new Date(Date.UTC(2020, startMonth, startDay + 13))
  const startLabel = `${MONTHS[nextStart.getUTCMonth()]} ${nextStart.getUTCDate()}`
  const endMonth = MONTHS[nextEnd.getUTCMonth()]
  const endLabel =
    nextStart.getUTCMonth() === nextEnd.getUTCMonth()
      ? String(nextEnd.getUTCDate())
      : `${endMonth} ${nextEnd.getUTCDate()}`

  return withMockElapsed({
    id: `w${number}`,
    label: `Week ${number} · ${startLabel}–${endLabel}`,
    status: 'draft',
    folded: false,
    open: true,
    days: WEEKDAYS.map((weekday, dayIdx) => {
      const date = new Date(
        Date.UTC(
          nextStart.getUTCFullYear(),
          nextStart.getUTCMonth(),
          nextStart.getUTCDate() + dayIdx,
        ),
      )
      return {
        label: `${weekday} ${date.getUTCDate()}`,
        kind: 'normal' as const,
        budget: lastWeek.days[dayIdx]?.budget ?? (dayIdx >= 5 ? 2 : 3),
        cards: [],
      }
    }),
  })
}

export const interviewCard = (
  tier: 'ai' | 'peer' | 'coach',
  id: string,
): TrayCard => {
  if (tier === 'ai') {
    return {
      id,
      title: 'AI mock interview',
      type: 'interview',
      meta: '🤖 AI mock · unlinked · ~1h',
      est: 1,
      aiMock: true,
      needsLink: true,
    }
  }
  if (tier === 'peer') {
    return {
      id,
      title: 'Schedule 2 peer mocks',
      type: 'interview',
      meta: '📋 scheduling task · auto-detected · ~0.5h',
      est: 0.5,
      link: '#peer-scheduling',
    }
  }
  return {
    id,
    title: 'Book a coach session',
    type: 'interview',
    meta: '📋 scheduling task · auto-detected · ~0.5h',
    est: 0.5,
    link: '#coach-booking',
  }
}
